import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Send, Users, Mail, CheckCircle2 } from 'lucide-react';
import {
  adminBroadcastPreview,
  adminBroadcastTest,
  adminBroadcastSend,
  adminListBroadcasts,
} from '@/lib/api';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

const AdminBroadcast = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('marketing');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [sending, setSending] = useState(false);
  const [pastBroadcasts, setPastBroadcasts] = useState([]);

  const loadPreview = async (aud) => {
    setPreviewLoading(true);
    try {
      const r = await adminBroadcastPreview(aud);
      setPreview(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to preview audience.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadPast = async () => {
    try {
      const r = await adminListBroadcasts();
      setPastBroadcasts(r.broadcasts || []);
    } catch {
      /* silent — non-critical */
    }
  };

  useEffect(() => { loadPreview(audience); }, [audience]);
  useEffect(() => { loadPast(); }, []);

  const doTest = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body are both required.'); return; }
    if (!testEmail.trim()) { toast.error('Enter a test email address first.'); return; }
    try {
      const r = await adminBroadcastTest({ subject: subject.trim(), body_text: body, to_email: testEmail.trim() });
      if (r.ok) toast.success(`Test email sent to ${testEmail}. Check your inbox.`);
      else toast.error(r.error || 'Test send failed.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed.');
    }
  };

  const doSend = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body are both required.'); return; }
    if (confirmPhrase !== 'SEND') { toast.error('Type SEND (all caps) to confirm.'); return; }
    if (audience === 'all' && !window.confirm(`You are about to email ALL ${preview?.count ?? '?'} accounts (not just marketing opt-ins). Continue?`)) return;
    setSending(true);
    try {
      const r = await adminBroadcastSend({
        subject: subject.trim(),
        body_text: body,
        audience,
        confirm_phrase: 'SEND',
      });
      toast.success(`Broadcast sent to ${r.sent_ok} / ${r.total}. Failures: ${r.sent_fail}.`);
      setSubject(''); setBody(''); setConfirmPhrase('');
      loadPast();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Broadcast failed.');
    } finally {
      setSending(false);
    }
  };

  const isAllAudience = audience === 'all';

  return (
    <div data-testid="admin-broadcast-page">
      <div>
        <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">Admin</div>
        <h1 className="font-display text-4xl mt-1">Broadcast email</h1>
        <p className="text-sm text-muted-sage mt-1 max-w-2xl">
          Compose a message and send it to your customers. Every email is wrapped in the Padel Draw brand template and includes a one-click unsubscribe link. Always send yourself a test first.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* Compose */}
        <div className="rounded-2xl border border-pd bg-surface p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-2">Subject</div>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Sunday's draw closes at 7pm"
            maxLength={200}
            className="w-full h-12 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-4 text-sm focus:border-[color:var(--pd-accent-yellow)] focus:outline-none"
            data-testid="broadcast-subject"
          />

          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mt-4 mb-2">Message body</div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            placeholder={'Write your message.\n\nBlank lines start a new paragraph. URLs (like https://thepadeldraw.co.uk/competitions) become clickable automatically.'}
            maxLength={20000}
            className="w-full rounded-xl bg-[color:var(--pd-bg)] border border-pd px-4 py-3 text-sm leading-relaxed focus:border-[color:var(--pd-accent-yellow)] focus:outline-none"
            data-testid="broadcast-body"
          />
          <div className="mt-1 text-[11px] text-muted-sage">Plain text only. The email will be wrapped in the Padel Draw brand template and include an unsubscribe link automatically.</div>

          {/* Test send */}
          <div className="mt-6 rounded-xl border border-pd bg-[color:var(--pd-bg)] p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-2">1. Send yourself a test</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 h-10 rounded-lg bg-surface border border-pd px-3 text-sm focus:border-[color:var(--pd-accent-yellow)] focus:outline-none"
                data-testid="broadcast-test-email"
              />
              <button
                type="button"
                onClick={doTest}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-pd bg-surface text-sm hover:border-[color:var(--pd-accent-yellow)]"
                data-testid="broadcast-test-send"
              >
                <Mail size={15} /> Send test
              </button>
            </div>
          </div>

          {/* Send */}
          <div className="mt-4 rounded-xl border border-pd bg-[color:var(--pd-bg)] p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-2">2. Send to your customers</div>
            {isAllAudience ? (
              <div
                className="mb-3 flex items-start gap-2 rounded-lg border border-[color:var(--pd-danger)]/50 bg-[color:var(--pd-danger)]/10 px-3 py-2"
                data-testid="broadcast-all-warning"
              >
                <AlertTriangle size={14} className="text-[color:var(--pd-danger)] mt-0.5 shrink-0" />
                <div className="text-xs text-[color:var(--pd-text)]">
                  <span className="text-[color:var(--pd-danger)] font-medium">Sending to ALL {preview?.count ?? '?'} accounts</span> — including those who did not opt in to marketing. Only use this for genuine service updates (T&amp;Cs changes, security notices, etc.). Marketing to non-opted-in users breaches UK PECR.
                </div>
              </div>
            ) : null}
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value.toUpperCase())}
                placeholder="Type SEND to confirm"
                className="flex-1 h-10 rounded-lg bg-surface border border-pd px-3 text-sm font-mono focus:border-[color:var(--pd-accent-yellow)] focus:outline-none"
                data-testid="broadcast-confirm-phrase"
              />
              <button
                type="button"
                onClick={doSend}
                disabled={sending || confirmPhrase !== 'SEND'}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[color:var(--pd-accent-yellow)] text-[#14251c] text-sm font-medium disabled:opacity-40"
                data-testid="broadcast-send"
              >
                <Send size={15} /> {sending ? 'Sending…' : `Send to ${preview?.count ?? '?'}`}
              </button>
            </div>
          </div>
        </div>

        {/* Audience picker + past */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-pd bg-surface p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-3">Audience</div>
            <div className="space-y-2">
              <label className={`block rounded-xl border p-3 cursor-pointer ${audience === 'marketing' ? 'border-[color:var(--pd-accent-yellow)] bg-[color:var(--pd-accent-yellow)]/5' : 'border-pd hover:border-[color:var(--pd-accent-yellow)]/50'}`}>
                <input
                  type="radio"
                  name="aud"
                  value="marketing"
                  checked={audience === 'marketing'}
                  onChange={() => setAudience('marketing')}
                  className="sr-only"
                  data-testid="broadcast-aud-marketing"
                />
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className={audience === 'marketing' ? 'text-[color:var(--pd-accent-yellow)]' : 'text-muted-sage'} />
                  <span className="text-sm font-medium">Marketing opt-ins only</span>
                </div>
                <p className="text-xs text-muted-sage mt-1 ml-6">Recommended. UK PECR-safe. Only users who ticked the marketing box.</p>
              </label>
              <label className={`block rounded-xl border p-3 cursor-pointer ${audience === 'all' ? 'border-[color:var(--pd-danger)] bg-[color:var(--pd-danger)]/5' : 'border-pd hover:border-[color:var(--pd-danger)]/50'}`}>
                <input
                  type="radio"
                  name="aud"
                  value="all"
                  checked={audience === 'all'}
                  onChange={() => setAudience('all')}
                  className="sr-only"
                  data-testid="broadcast-aud-all"
                />
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} className={audience === 'all' ? 'text-[color:var(--pd-danger)]' : 'text-muted-sage'} />
                  <span className="text-sm font-medium">All accounts</span>
                </div>
                <p className="text-xs text-muted-sage mt-1 ml-6">Use ONLY for service updates (T&amp;Cs, security). Bypasses marketing opt-in.</p>
              </label>
            </div>

            <div className="mt-4 pt-4 border-t border-pd">
              <div className="flex items-center gap-2 text-sm">
                <Users size={14} className="text-[color:var(--pd-gold)]" />
                <span className="font-mono text-[color:var(--pd-text)]" data-testid="broadcast-audience-count">
                  {previewLoading ? 'Counting…' : `${preview?.count ?? 0} recipient${(preview?.count ?? 0) === 1 ? '' : 's'}`}
                </span>
              </div>
              {preview?.sample?.length ? (
                <div className="mt-2 text-[11px] text-muted-sage font-mono">
                  Sample: {preview.sample.slice(0, 3).map((s) => s.email).join(', ')}{preview.sample.length > 3 ? '…' : ''}
                </div>
              ) : null}
            </div>
          </div>

          <div className="rounded-2xl border border-pd bg-surface p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-3">Recent broadcasts</div>
            {pastBroadcasts.length === 0 ? (
              <div className="text-xs text-muted-sage" data-testid="broadcast-history-empty">No broadcasts sent yet.</div>
            ) : (
              <div className="space-y-2 text-xs">
                {pastBroadcasts.slice(0, 8).map((b) => (
                  <div key={b.id} className="rounded-lg border border-pd p-2" data-testid={`broadcast-history-${b.id}`}>
                    <div className="text-[color:var(--pd-text)] font-medium truncate">{b.subject}</div>
                    <div className="font-mono text-[10px] text-muted-sage mt-1">
                      {b.audience} · sent {b.sent_ok}/{b.total_recipients} · {formatDateTime(b.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBroadcast;
